import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Clock,
  CheckCircle2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Bookmark,
  Save,
  Send,
  X,
  Loader2,
  Flag
} from 'lucide-react';
import { startAssessment, submitAnswer, submitAssessmentFinal } from '../api/api';

// Toast Component inside the same file
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'error' ? 'bg-red-500' : 
                  type === 'success' ? 'bg-green-500' : 
                  type === 'warning' ? 'bg-yellow-500' : 'bg-blue-500';

  const icon = type === 'error' ? '✕' : 
               type === 'success' ? '✓' : 
               type === 'warning' ? '⚠' : 'ℹ';

  return (
    <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg animate-slide-in ${bgColor} text-white min-w-[280px] max-w-md`}>
      <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
        <span className="text-sm font-bold">{icon}</span>
      </div>
      <span className="text-sm flex-1">{message}</span>
      <button 
        onClick={onClose} 
        className="text-white hover:text-gray-200 text-xl leading-none"
      >
        ×
      </button>
    </div>
  );
};

// Logo component to prevent infinite loops
const Logo = () => {
  const [imageError, setImageError] = useState(false);
  
  if (imageError) {
    return (
      <div className="h-8 md:h-9 w-8 md:w-9 bg-green-600 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
        N
      </div>
    );
  }
  
  return (
    <img
      src="/logo.png"
      className="h-8 md:h-9 flex-shrink-0"
      alt="Logo"
      onError={() => setImageError(true)}
    />
  );
};

const Assessment = ({ assessmentData: propAssessmentData }) => {
  const navigate = useNavigate();
  const { assessmentId: paramAssessmentId } = useParams();
  const location = useLocation();
  const stateData = location.state || {};
  
  // State for assessment data
  const [assessment, setAssessment] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [userAnswers, setUserAnswers] = useState({});
  
  // UI States
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [markedQuestions, setMarkedQuestions] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('--:--');
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [score, setScore] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [assessmentResults, setAssessmentResults] = useState(null);
  
  // Toast state
  const [toast, setToast] = useState(null);
  
  // Refs to prevent multiple API calls
  const hasFetchedRef = useRef(false);
  const timerRef = useRef(null);
  const submittedQuestionsRef = useRef(new Set());

  // Get assessment ID from props, state, or params
  const assessmentId = propAssessmentData?.assessmentId || 
                       stateData.assessmentId || 
                       paramAssessmentId;

  // Show toast function
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Fetch assessment data on mount - Only once
  useEffect(() => {
    if (hasFetchedRef.current) return;
    
    const fetchAssessment = async () => {
      if (!assessmentId) {
        setError('No assessment ID provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        
        console.log('Fetching assessment with ID:', assessmentId);
        const response = await startAssessment(assessmentId);
        console.log('Assessment Response:', response);

        if (response && response.success) {
          const data = response.data;
          
          setAssessment(data.assessment);
          setAttempt(data.attempt);
          setQuestions(data.questions || []);
          setUserAnswers(data.user_answers || {});
          setAttemptId(data.attempt?.id);

          const timeLimitSeconds = (data.assessment?.time_limit_minutes || 0) * 60;
          setTotalSeconds(timeLimitSeconds);
          
          const minutes = Math.floor(timeLimitSeconds / 60);
          const seconds = timeLimitSeconds % 60;
          setTimeRemaining(`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);

          const initialAnswers = {};
          const initialMarked = [];
          
          if (data.user_answers && Object.keys(data.user_answers).length > 0) {
            Object.keys(data.user_answers).forEach(key => {
              const questionIndex = data.questions.findIndex(q => q.id === parseInt(key));
              if (questionIndex !== -1) {
                initialAnswers[questionIndex] = data.user_answers[key];
                submittedQuestionsRef.current.add(questionIndex);
              }
            });
          }
          
          if (data.attempt?.marked_for_review && Array.isArray(data.attempt.marked_for_review)) {
            data.attempt.marked_for_review.forEach(qId => {
              const questionIndex = data.questions.findIndex(q => q.id === qId);
              if (questionIndex !== -1) {
                initialMarked.push(questionIndex);
              }
            });
          }
          
          setAnswers(initialAnswers);
          setMarkedQuestions(initialMarked);
          setQuestionStartTime(Date.now());

          hasFetchedRef.current = true;

        } else {
          setError(response?.message || 'Failed to load assessment');
        }
      } catch (err) {
        console.error('Error fetching assessment:', err);
        setError(err.message || 'Failed to load assessment');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssessment();
  }, [assessmentId]);

  // Timer effect
  useEffect(() => {
    if (isLoading || !assessment) return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      setTotalSeconds(prev => {
        if (prev <= 0) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          handleSubmit();
          return 0;
        }
        const minutes = Math.floor(prev / 60);
        const seconds = prev % 60;
        setTimeRemaining(`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isLoading, assessment]);

  // Handle answer selection
  const handleAnswerSelect = async (questionIndex, optionIndex) => {
    if (showResult || isSubmitting || submittingAnswer) return;
    
    const question = questions[questionIndex];
    if (!question) return;

    const answerKey = `${questionIndex}-${optionIndex}`;
    if (submittedQuestionsRef.current.has(answerKey)) {
      console.log('Answer already submitted, skipping duplicate call');
      return;
    }

    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: optionIndex
    }));

    const answerValue = String.fromCharCode(65 + optionIndex);
    const isMarked = markedQuestions.includes(questionIndex);

    try {
      setSubmittingAnswer(true);
      const response = await submitAnswer(
        attemptId,
        question.id,
        answerValue,
        timeSpent,
        isMarked
      );
      console.log('Answer submitted successfully:', response);
      
      submittedQuestionsRef.current.add(answerKey);
      setQuestionStartTime(Date.now());
      
    } catch (error) {
      console.error('Failed to submit answer:', error);
      showToast('Failed to save your answer. Please try again.', 'error');
    } finally {
      setSubmittingAnswer(false);
    }
  };

  // Navigate to question
  const navigateToQuestion = (index) => {
    if (index < 0 || index >= (questions?.length || 0)) return;
    setCurrentQuestion(index);
    setQuestionStartTime(Date.now());
  };

  // Mark for review
  const handleMarkReview = async () => {
    const question = questions[currentQuestion];
    if (!question || !attemptId) return;

    const isCurrentlyMarked = markedQuestions.includes(currentQuestion);
    let newMarkedQuestions;
    
    if (isCurrentlyMarked) {
      newMarkedQuestions = markedQuestions.filter(q => q !== currentQuestion);
    } else {
      newMarkedQuestions = [...markedQuestions, currentQuestion];
    }
    
    setMarkedQuestions(newMarkedQuestions);
    
    try {
      const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
      const answerValue = answers[currentQuestion] !== undefined 
        ? String.fromCharCode(65 + answers[currentQuestion]) 
        : null;
      
      await submitAnswer(
        attemptId,
        question.id,
        answerValue,
        timeSpent,
        !isCurrentlyMarked
      );
      
      showToast(isCurrentlyMarked ? 'Question unmarked for review' : 'Question marked for review', 'success');
    } catch (error) {
      console.error('Failed to update mark for review:', error);
      showToast('Failed to update review status', 'error');
    }
  };

  // Save progress
  const handleSaveProgress = async () => {
    if (!attemptId) return;
    
    try {
      setSubmittingAnswer(true);
      let savedCount = 0;
      
      for (let i = 0; i < questions.length; i++) {
        const question = questions[i];
        if (!question) continue;
        
        const answerKey = `${i}-${answers[i]}`;
        if (submittedQuestionsRef.current.has(answerKey)) continue;
        
        const answerValue = answers[i] !== undefined 
          ? String.fromCharCode(65 + answers[i]) 
          : null;
        const isMarked = markedQuestions.includes(i);
        const timeSpent = 0;
        
        await submitAnswer(
          attemptId,
          question.id,
          answerValue,
          timeSpent,
          isMarked
        );
        
        if (answers[i] !== undefined) {
          submittedQuestionsRef.current.add(answerKey);
          savedCount++;
        }
      }
      
      showToast(`Progress saved successfully! ${savedCount} answers updated.`, 'success');
      console.log('All progress saved');
    } catch (error) {
      console.error('Failed to save progress:', error);
      showToast('Failed to save progress. Please try again.', 'error');
    } finally {
      setSubmittingAnswer(false);
    }
  };

  // Calculate score
  const calculateScore = () => {
    let correct = 0;
    questions.forEach((q, index) => {
      if (answers[index] !== undefined && answers[index] !== null) {
        if (q.correct_answer_index !== undefined) {
          if (answers[index] === q.correct_answer_index) {
            correct++;
          }
        }
      }
    });
    const calculatedScore = Math.round((correct / questions.length) * 100);
    setScore(calculatedScore);
    return calculatedScore;
  };

  // Submit assessment
  const handleSubmit = async () => {
    if (isSubmitting) return;
    
    const answered = Object.keys(answers).length;
    const total = questions?.length || 0;
    const unanswered = total - answered;

    if (unanswered > 0) {
      setShowConfirmDialog(true);
      return;
    }

    await submitAssessmentFinalCall();
  };
// Update the submitAssessmentFinalCall function with cleaner toast message

const submitAssessmentFinalCall = async () => {
  setIsSubmitting(true);
  setShowResult(true);
  const calculatedScore = calculateScore();

  try {
    // Submit any pending answers
    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];
      if (!question) continue;
      
      const answerKey = `${i}-${answers[i]}`;
      if (submittedQuestionsRef.current.has(answerKey)) continue;
      
      const answerValue = answers[i] !== undefined 
        ? String.fromCharCode(65 + answers[i]) 
        : null;
      const isMarked = markedQuestions.includes(i);
      const timeSpent = Math.floor((Date.now() - questionStartTime) / questions.length);
      
      try {
        await submitAnswer(
          attemptId,
          question.id,
          answerValue,
          timeSpent,
          isMarked
        );
      } catch (error) {
        console.error(`Failed to submit answer for question ${i}:`, error);
      }
    }

    const response = await submitAssessmentFinal(attemptId);
    console.log('Assessment submitted successfully:', response);
    
    setAssessmentResults(response.data);

    const passed = calculatedScore >= (assessment?.passing_score || 50);
    
    setIsSubmitting(false);
    setShowConfirmDialog(false);
    
    // ✅ Clean toast message - just show submission status
    showToast('Assessment submitted successfully!', 'success');
    
    // Navigate after toast with a small delay
    setTimeout(() => {
      navigate('/dashboard', {
        state: {
          activeComponent: 'mycourses'
        }
      });
    }, 1500);
    
  } catch (error) {
    console.error('Error submitting assessment:', error);
    showToast('Failed to submit assessment. Please try again.', 'error');
    setIsSubmitting(false);
    setShowConfirmDialog(false);
  }
};

  // Get current question data
  const currentQuestionData = questions?.[currentQuestion];
  const answeredCount = Object.keys(answers).length;
  const totalQuestions = questions?.length || 0;
  const unansweredCount = totalQuestions - answeredCount;
  const isMarked = markedQuestions.includes(currentQuestion);

  const getQuestionStatus = (index) => {
    if (index === currentQuestion) return 'current';
    if (markedQuestions.includes(index)) return 'marked';
    if (answers[index] !== undefined && answers[index] !== null) return 'answered';
    return 'unanswered';
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading assessment...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !assessment) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-2xl shadow-sm max-w-md">
          <div className="text-6xl mb-4">😕</div>
          <h3 className="text-xl font-semibold text-gray-700">Failed to load assessment</h3>
          <p className="text-gray-500 mt-2">{error || "Assessment not found"}</p>
          <button
            onClick={() => navigate('/dashboard', { state: { activeComponent: 'mycourses' } })}
            className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Back to Courses
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fb]">
      {/* Toast Notification */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}

      {/* Confirm Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl animate-slideUp">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={32} className="text-yellow-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Unanswered Questions</h3>
              <p className="text-gray-500 mt-2">
                You have {unansweredCount} unanswered question{unansweredCount > 1 ? 's' : ''}. 
                Are you sure you want to submit?
              </p>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowConfirmDialog(false)}
                  className="flex-1 border border-gray-300 px-6 py-2.5 rounded-lg font-medium hover:bg-gray-50 transition"
                >
                  Continue
                </button>
                <button
                  onClick={submitAssessmentFinalCall}
                  className="flex-1 bg-green-700 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-green-800 transition"
                >
                  Submit Anyway
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b sticky top-0  shadow-sm">
        <div className="h-16 flex items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-3 min-w-0">
            <button 
              onClick={() => navigate('/dashboard', { state: { activeComponent: 'mycourses' } })}
              className="p-2 hover:bg-gray-100 rounded-lg transition flex-shrink-0"
            >
              <ChevronLeft size={20} />
            </button>
            
            <Logo />
            
            <div className="min-w-0">
              <p className="text-xs text-gray-500 truncate">{assessment?.title || 'Assessment'}</p>
              <h3 className="font-semibold text-xs md:text-sm lg:text-base truncate">
                {assessment?.title || 'Assessment'}
              </h3>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-4 lg:gap-6">
            <div className="hidden sm:block">
              <p className="text-xs text-gray-500 text-center">
                Question {currentQuestion + 1} of {totalQuestions}
              </p>
              <div className="w-20 md:w-28 lg:w-40 h-1.5 rounded-full bg-gray-200 mt-1">
                <div 
                  className="bg-green-600 h-full rounded-full transition-all duration-300" 
                  style={{ width: `${((currentQuestion + 1) / totalQuestions) * 100}%` }}
                />
              </div>
            </div>

            <span className="text-xs md:text-sm hidden lg:block whitespace-nowrap">
              Passing Score: <b className="text-green-700">{assessment?.passing_score || 50}%</b>
            </span>

            <div className="bg-yellow-400 px-2.5 md:px-4 py-1.5 md:py-2 rounded-lg font-bold text-xs md:text-sm flex items-center gap-1 md:gap-2 whitespace-nowrap">
              <Clock size={14} className="md:w-4 md:h-4" />
              <span>{timeRemaining}</span>
            </div>

            <div className="bg-green-100 text-green-700 px-2 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm hidden md:block whitespace-nowrap">
              Pass: {assessment?.passing_score || 50}%
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-[1450px] mx-auto px-3 md:px-5 py-4 md:py-5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5">
          
          {/* Left Column - Question Card */}
          <div className="lg:col-span-9 space-y-4 md:space-y-5">
            <div className="bg-white rounded-xl shadow-sm border p-4 md:p-6">
              <div className="flex flex-col sm:flex-row justify-between gap-2 sm:gap-0">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                  {assessment?.title || 'Assessment'}
                </h2>
                <div className="flex items-center gap-2">
                  <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs md:text-sm whitespace-nowrap">
                    {currentQuestionData?.question_type === 'true_false' ? 'True/False' : 'Multiple Choice'}
                  </span>
                  {isMarked && (
                    <span className="bg-yellow-400 text-yellow-800 px-3 py-1 rounded-full text-xs md:text-sm flex items-center gap-1 whitespace-nowrap">
                      <Flag size={14} />
                      Marked
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-5 md:mt-7">
                <span className="bg-green-700 text-white px-3 md:px-4 py-1 rounded-full text-xs font-medium">
                  Question {currentQuestion + 1}
                </span>
                <h3 className="mt-3 md:mt-4 text-lg md:text-xl font-semibold text-gray-800">
                  {currentQuestionData?.question_text || 'Question not available'}
                </h3>
              </div>

              <div className="mt-6 md:mt-8 space-y-3 md:space-y-4">
                {currentQuestionData?.options?.map((option, index) => {
                  const isSelected = answers[currentQuestion] === index;
                  
                  let cardClass = 'border rounded-lg p-3 md:p-5 flex items-center gap-3 md:gap-5 cursor-pointer transition-all duration-200 ';
                  
                  if (isSelected) {
                    cardClass += 'border-green-600 bg-green-50';
                  } else {
                    cardClass += 'border-gray-200 hover:border-green-400 hover:bg-gray-50';
                  }

                  const letter = String.fromCharCode(65 + index);

                  return (
                    <div
                      key={index}
                      className={cardClass}
                      onClick={() => handleAnswerSelect(currentQuestion, index)}
                    >
                      <div className={`w-7 h-7 md:w-8 md:h-8 rounded-md border flex items-center justify-center font-medium text-sm flex-shrink-0 ${
                        isSelected ? 'border-green-600 text-green-700 bg-green-100' : 'border-gray-300 text-gray-600'
                      }`}>
                        {letter}
                      </div>
                      <div className="flex-1 text-sm md:text-base text-gray-700">{option}</div>
                      {isSelected && (
                        <div className="flex-shrink-0">
                          <CheckCircle2 size={20} className="text-green-600" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Bottom Buttons */}
            <div className="mt-4 md:mt-6">
              <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0">
                <div className="flex flex-wrap gap-2 md:gap-3">
                  <button
                    onClick={() => navigateToQuestion(currentQuestion - 1)}
                    disabled={currentQuestion === 0 || submittingAnswer}
                    className={`border px-4 md:px-6 py-2 md:py-2.5 rounded-lg text-sm md:text-base flex items-center gap-2 transition ${
                      currentQuestion === 0 || submittingAnswer
                        ? 'opacity-50 cursor-not-allowed text-gray-400' 
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <ChevronLeft size={18} />
                    Previous
                  </button>

                  <button
                    onClick={handleMarkReview}
                    disabled={submittingAnswer}
                    className={`px-4 md:px-6 py-2 md:py-2.5 rounded-lg text-sm md:text-base flex items-center gap-2 transition ${
                      isMarked 
                        ? 'bg-yellow-500 text-white hover:bg-yellow-600' 
                        : 'bg-yellow-400 hover:bg-yellow-500 text-gray-800'
                    } ${submittingAnswer ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Bookmark size={18} />
                    {isMarked ? 'Unmark' : 'Mark for Review'}
                  </button>

                  <button
                    onClick={handleSaveProgress}
                    disabled={submittingAnswer}
                    className={`border px-4 md:px-6 py-2 md:py-2.5 rounded-lg text-sm md:text-base flex items-center gap-2 hover:bg-gray-50 transition text-gray-700 ${
                      submittingAnswer ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {submittingAnswer ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                    Save Progress
                  </button>
                </div>

                <button
                  onClick={() => navigateToQuestion(currentQuestion + 1)}
                  disabled={currentQuestion === totalQuestions - 1 || submittingAnswer}
                  className={`bg-green-700 text-white px-5 md:px-7 py-2 md:py-2.5 rounded-lg text-sm md:text-base flex items-center gap-2 transition ${
                    currentQuestion === totalQuestions - 1 || submittingAnswer
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:bg-green-800'
                  }`}
                >
                  {submittingAnswer ? <Loader2 size={18} className="animate-spin" /> : 'Next'}
                  {!submittingAnswer && <ChevronRight size={18} />}
                </button>
              </div>

              <div className="flex justify-end mt-4 md:mt-5">
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || submittingAnswer}
                  className="bg-green-700 text-white px-6 md:px-8 py-2.5 md:py-3.5 rounded-lg font-semibold text-sm md:text-base flex items-center gap-2 hover:bg-green-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Submit Assessment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="lg:col-span-3 space-y-4 md:space-y-5">
            {/* Timer Card */}
            <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl p-4 md:p-6 shadow-sm">
              <p className="text-xs font-semibold text-gray-700">TIME REMAINING</p>
              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mt-1">{timeRemaining}</h1>
              <p className="text-sm text-gray-700 mt-1">{assessment?.time_limit_minutes || 0} minutes total</p>
            </div>

            {/* Summary Card */}
            <div className="bg-white rounded-xl border p-4 md:p-5 shadow-sm">
              <h3 className="font-bold text-gray-800 mb-3 md:mb-4 text-sm md:text-base">ASSESSMENT SUMMARY</h3>
              <div className="space-y-2 md:space-y-3 text-sm md:text-base">
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Questions</span>
                  <b className="text-gray-800">{totalQuestions}</b>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>Answered</span>
                  <b>{answeredCount}</b>
                </div>
                <div className="flex justify-between text-red-500">
                  <span>Unanswered</span>
                  <b>{unansweredCount}</b>
                </div>
                <div className="flex justify-between text-yellow-600">
                  <span>Marked</span>
                  <b>{markedQuestions.length}</b>
                </div>
              </div>
            </div>

            {/* Question Navigator */}
            <div className="bg-white border rounded-xl p-4 md:p-5 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-3 md:mb-4 text-sm md:text-base">
                QUESTION NAVIGATOR
              </h3>
              <div className="grid grid-cols-5 gap-2 md:gap-3">
                {Array.from({ length: totalQuestions }).map((_, i) => {
                  const status = getQuestionStatus(i);
                  let bgClass = 'border border-gray-300 hover:bg-gray-50 text-gray-700';
                  
                  if (status === 'current') {
                    bgClass = 'bg-green-700 text-white border-green-700';
                  } else if (status === 'marked') {
                    bgClass = 'bg-yellow-400 text-gray-800 border-yellow-400';
                  } else if (status === 'answered') {
                    bgClass = 'bg-green-100 text-green-700 border-green-300';
                  }
                  
                  return (
                    <button
                      key={i}
                      className={`h-9 md:h-11 rounded-lg text-sm font-semibold transition-all ${bgClass}`}
                      onClick={() => navigateToQuestion(i)}
                      disabled={submittingAnswer}
                    >
                      {i + 1}
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 flex flex-wrap gap-3 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-green-700"></div>
                  <span className="text-gray-600">Current</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-green-100 border border-green-300"></div>
                  <span className="text-gray-600">Answered</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-yellow-400"></div>
                  <span className="text-gray-600">Marked</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded border border-gray-300"></div>
                  <span className="text-gray-600">Unanswered</span>
                </div>
              </div>
            </div>
            
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || submittingAnswer}
              className="w-full bg-green-700 text-white py-3 md:py-4 rounded-lg font-semibold text-sm md:text-base flex items-center justify-center gap-2 hover:bg-green-800 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Submit Assessment
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Assessment;